import GetQueryParams from "components/common/services/GetQueryParams";
import {
  createQueryParams,
  filterAndMerge,
  getQueryParamsFromRedux,
} from "components/common/utlis/HelperFunctions";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import type { RootState } from "store";
import { createFilterConfig } from "./Filters";
import {
  clearDestinationPath,
  setDestinationPath,
} from "features/NavigationSlice";
import { ApiCall } from "components/common/services/ApiServices";
import { DELETE_WHATSAPP_USER, GET_WHATSAPP_FORM } from "routes/ApiEndpoints";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import { Link, useNavigate } from "react-router-dom";
import PaginationNew from "components/common/atoms/PaginationNew";
import AccessControl from "components/common/services/RolesAndPermissions/AccessControl";
import Title from "components/common/atoms/Title";
import SearchFields from "components/common/atoms/SearchFields";
import ResetBtn from "components/common/atoms/ResetBtn";
import Sorting from "components/common/atoms/Sorting";
import ActionIconWrapper from "components/common/atoms/ActionIconWrapper";
import DeleteIcon from "static/images/DeleteIcon";
import LinkTo from "components/common/atoms/LinkTo";
import { AJ_WEB_CONNECTOR } from "Constants";
import CustomNotify from "components/common/atoms/CustomNotify";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import SpinnerWrapper from "components/common/atoms/SpinnerWrapper";
import NoRecords from "components/common/atoms/NoRecords";
import ModalPopup from "components/common/atoms/ModalPopup";
export interface WhatsAppUserData {
  id: number;
  user_id: number;
  user_name: string;
  phone_number: string;
  updated_at: Date | null;
  created_at: Date | null;
}

export interface SearchProps {
  userName: string;
  phoneNumber: string;
  from: Date | null | string;
  to: Date | null | string;
  currentPage: number;
  recordsPerPage: number;
  orderByColumn: string;
  orderIn: "" | "asc" | "desc";
}

const ManageWhatsAppUsers: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate(); // Not strictly used
  const [loading, setLoading] = useState(false);
  const [whatsAppUsers, setWhatsAppUsers] = useState<WhatsAppUserData[]>([]);
  const [dropdownData, setDropdownData] = useState<any>({});

  const [paginationData, setPaginationData] = useState({
    totalPages: 1,
    totalRecords: 1,
    cursor: null,
  });

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDeleteId, setUserToDeleteId] = useState<number | null>(null);

  const destinationPath = useSelector(
    (state: RootState) => state.navigation.destinationPath
  );

  const getUrlParams = GetQueryParams();
  const paramFilter = getUrlParams.get("filter");
  const queryParams = getQueryParamsFromRedux(destinationPath, paramFilter);

  const defaultSearchData: SearchProps = {
    userName: "",
    phoneNumber: "",
    from: "",
    to: "",
    currentPage: 1,
    recordsPerPage: 10,
    orderByColumn: "",
    orderIn: "",
  };

  const filteredSearchData = filterAndMerge(defaultSearchData, queryParams);

  const [searchData, setSearchData] = useState<SearchProps>({
    ...defaultSearchData,
    ...filteredSearchData,
  });


  useEffect(() => {
    dispatch(clearDestinationPath());
    const filterData = { ...searchData };
    const searchUrl = createQueryParams(filterData);
    dispatch(setDestinationPath(searchUrl));
    const delayDebounceFn = setTimeout(() => {
      fetchDetails(filterData);
    }, 1000);
    return () => clearTimeout(delayDebounceFn);
  }, [searchData, dispatch]);


  const fetchDetails = async (filterData: any) => {
    setLoading(true);
    const data = {
      search: filterData,
      type: "whatsapp-user",
    };

    try {
      const response = await ApiCall.service(
        GET_WHATSAPP_FORM,
        "POST",
        data,
        false,
        AJ_WEB_CONNECTOR
      );

      if (response?.status === 200) {
        setWhatsAppUsers(response?.data?.usersData || []);
        setPaginationData({
          totalPages: response?.data?.paginationData?.totalPages || 1,
          totalRecords: response?.data?.paginationData?.totalRecords || 0,
          cursor: response?.data?.paginationData?.cursor || null,
        });
      } else {
        CustomNotify({
          type: "error",
          message: t("Some error occured"),
        });
        setWhatsAppUsers([]); // Clear users on error or non-200 status
        setPaginationData({ totalPages: 1, totalRecords: 0, cursor: null });
      }
    } catch (error) {
      console.error("Error fetching WhatsApp users:", error);
      setWhatsAppUsers([]);
      setPaginationData({ totalPages: 1, totalRecords: 0, cursor: null });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteModal = (userId: number) => {
    setUserToDeleteId(userId);
    setShowDeleteModal(true);
  };

  const handleModalClose = () => {
    setShowDeleteModal(false);
    setUserToDeleteId(null);
  };

  const handleConfirmDelete = async () => {
    if (userToDeleteId === null) {return;}

    try {
      const response = await ApiCall.getService(
        DELETE_WHATSAPP_USER + '/' + userToDeleteId,
        "GET",
        AJ_WEB_CONNECTOR,
        true
      );

      if (response?.status === 200) {
        CustomNotify({
          type: "success",
          message: t(response?.msg),
        });
        fetchDetails(searchData);
      } else {
        CustomNotify({
          type: "error", // Changed to error for failed deletion
          message: response?.data?.msg || t("Failed to delete WhatsApp user"),
        });
      }
    } catch (error: any) {
      console.error("Error deleting WhatsApp user:", error);
      CustomNotify({
        type: "error",
        message: error.response?.data?.msg || t("Failed to delete WhatsApp user"),
      });
    } finally {
      handleModalClose();
    }
  };

  const filters = React.useMemo(
    () => createFilterConfig(dropdownData),
    [dropdownData]
  );

  const handleSearchChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [name]: value,
    }));
  };

  const handlePageChange = (pageNumber: number) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: pageNumber,
    }));
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [fieldName]: selectedOption,
    }));
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setSearchData((prev) => ({
      ...prev,
      recordsPerPage: newItemsPerPage,
      currentPage: 1,
    }));
  };

  const handleReset = async () => {
    dispatch(clearDestinationPath());
    setSearchData(defaultSearchData);
    // fetchDetails will be called by useEffect due to searchData change
  };

  const handleDateChange = (date: Date | null, fieldName: string) => {
    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      [fieldName]: date,
    }));
  };

  const handleOrderByColumn = (column: string) => {
    let newOrder: "" | "asc" | "desc" = "asc"; // Default to asc for new column
    let newColumn = column;

    if (searchData.orderByColumn === column) {
      if (searchData.orderIn === "asc") {
        newOrder = "desc";
      } else if (searchData.orderIn === "desc") {
        newOrder = ""; // Reset sorting
        newColumn = ""; // Reset column
      }
      // If orderIn is "", it will become "asc" by the initial newOrder value
    }

    setSearchData((prevSearch) => ({
      ...prevSearch,
      currentPage: 1,
      orderByColumn: newColumn,
      orderIn: newOrder,
    }));
  };

  return (
    <AccessControl
      requiredPermissions={[
        {
          permission: "Whatsapp account",
          read: true,
        },
      ]}
      renderNoAccess={true}
    >
      <div className={`main-container`}>
        <Title title={t("Whatsapp accounts")} />
        <div className="form-height-dashboard">
          <div className="position-relative tableMainWrapper">
            <div className="row">
              <div className="ManageCreateBtn">
                <AccessControl
                  requiredPermissions={[
                    {
                      permission: "Whatsapp account",
                      create: true,
                    },
                  ]}
                >
                  <LinkTo
                    pagelink="/add/whatsapp-user"
                    title={t("Add user")}
                    icon={faPlus}
                    className="me-3"
                  />
                </AccessControl>
              </div>
            </div>

            <div className="table-responsive tableSection">
              <table className="table manageWhatsappUsers border-0 mb-0">
                <thead>
                  <tr className="tableSearchFields">
                    <SearchFields
                      searchData={searchData}
                      filters={filters}
                      handleSearchChange={handleSearchChange}
                      handleSelectChange={handleSelectChange}
                      handleDateChange={handleDateChange}
                    />
                    <th className="border-0">
                      <div className="d-flex align-items-center w-100">
                        <ResetBtn handleResetClick={handleReset} />
                      </div>
                    </th>
                  </tr>
                </thead>
              </table>
              <div className="tableContainerWithoutTabs scrollBarDesign">
                <table className="table table-hover manageWhatsappUsers">
                  <thead>
                    <tr className="TableHeader">
                      <th onClick={() => handleOrderByColumn("user_name")}> {/* Matched with DB column */}
                        {t("Username")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "user_name"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <th onClick={() => handleOrderByColumn("phone_number")}> {/* Matched with DB column */}
                        {t("Phone number")}
                        <Sorting
                          sortType={
                            searchData.orderByColumn === "phone_number"
                              ? searchData.orderIn
                              : ""
                          }
                        />
                      </th>
                      <AccessControl
                        requiredPermissions={[
                          {
                            permission: "WhatsAppUser",
                            update: true,
                            delete: true,
                            read: true,
                          },
                        ]}
                        actions={true}
                        strict={false} // Allows if any of the permissions match
                      >
                        <th>{t("Action")}</th>
                      </AccessControl>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <SpinnerWrapper headerLength={3} />
                    ) : (
                      <>
                        {whatsAppUsers && whatsAppUsers.length > 0 ? (
                          whatsAppUsers.map(
                            (user: WhatsAppUserData) => (
                              <tr
                                key={user.id}
                                className="border box-shadow align-middle"
                              >
                                <td
                                  className="text-break"
                                  data-label={t("User Name")}
                                >
                                  {user.user_name}
                                </td>
                                <td
                                  className="text-break"
                                  data-label={t("Phone Number")}
                                >
                                  {user.phone_number}
                                </td>
                                <AccessControl
                                  requiredPermissions={[
                                    {
                                      permission: "WhatsAppUser",
                                      update: true,
                                      delete: true,
                                      read: true,
                                    },
                                  ]}
                                  actions={true}
                                  strict={false}
                                >
                                  <td className="table-action-icons px-2">
                                    <ActionIconWrapper
                                      initialVisibleCount={1}
                                    >
                                      <AccessControl requiredPermissions={[{ permission: "WhatsAppUser", delete: true }]}>
                                        <span
                                          className="delete-icon-wrapper"
                                          title={t("Delete")}
                                          onClick={() =>
                                            handleOpenDeleteModal(user.id)
                                          }
                                          style={{ cursor: "pointer" }}
                                          role="button"
                                          tabIndex={0}
                                          onKeyPress={(e) => e.key === 'Enter' && handleOpenDeleteModal(user.id)}
                                        >
                                          <DeleteIcon />
                                        </span>
                                      </AccessControl>
                                      {/* Add Edit icon here if needed, wrapped in AccessControl */}
                                    </ActionIconWrapper>
                                  </td>
                                </AccessControl>
                              </tr>
                            )
                          )
                        ) : (
                          !loading && <NoRecords headerLength={3} />
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              </div>
              {!loading && paginationData.totalRecords > 0 && (
                <div className="pagination d-block">
                  <PaginationNew
                    currentPage={searchData.currentPage}
                    totalPages={paginationData.totalPages}
                    totalRecords={paginationData.totalRecords}
                    recordsPerPage={searchData.recordsPerPage}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div
          className="col-md-6 align-self-center"
          style={{ padding: "1vw 0" }}
        >
          <Link to="/dashboard" className=" back-btn text-decoration-underline">
            {t("Back")}
          </Link>
        </div>
      </div>
      <ModalPopup
        show={showDeleteModal}
        onHide={handleModalClose}
        title={t("Delete confirmation")}
        body={t("Are you sure want to delete") + "?"}
        onCloseButtonClick={handleModalClose}
        onConfirmButtonClick={handleConfirmDelete}
        closeTitle={t("No")}
        confirmTitle={t("Yes")}
      />
    </AccessControl>
  );
};

export default translate(ManageWhatsAppUsers);